// theme.js - single source of truth for light/dark theme across the whole app.
// All components should: import { useTheme, tokens, ThemeToggleButton } from './theme';
import { useEffect, useState } from 'react';

export const THEME_KEY = 'wc_theme';
const EVT = 'wc_theme_change';

function readTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : 'dark';
  } catch {
    return 'dark';
  }
}

// Hook: every call shares state via localStorage + a window event.
// Flipping theme in one component instantly updates every other mounted component.
export function useTheme() {
  const [theme, setThemeState] = useState(readTheme);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === THEME_KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
        setThemeState(e.newValue);
      }
    };
    const onCustom = (e) => {
      const v = e?.detail;
      if (v === 'light' || v === 'dark') setThemeState(v);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(EVT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(EVT, onCustom);
    };
  }, []);

  const setTheme = (next) => {
    // Accept either a raw value or a React-style updater function
    setThemeState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      const val = resolved === 'light' ? 'light' : 'dark';
      try { localStorage.setItem(THEME_KEY, val); } catch {}
      try { window.dispatchEvent(new CustomEvent(EVT, { detail: val })); } catch {}
      return val;
    });
  };

  return [theme, setTheme];
}

// Canonical color tokens. Every component that needs a color should pull from here.
export function tokens(theme) {
  const dark = theme === 'dark';
  return {
    dark,
    // surfaces
    page:         dark ? '#0f172a' : '#f8fafc',
    card:         dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
    cardAlt:      dark ? '#111827' : '#fafafa',
    cardAlt2:     dark ? '#1e293b' : '#f1f5f9',
    // borders
    border:       dark ? '#1f2937' : '#e5e7eb',
    borderStrong: dark ? '#334155' : '#cbd5e1',
    // text
    text:         dark ? '#e5e7eb' : '#0f172a',
    textStrong:   dark ? '#f8fafc' : '#0f172a',
    muted:        dark ? '#94a3b8' : '#64748b',
    mutedSoft:    dark ? '#cbd5e1' : '#475569',
    // inputs
    inputBg:      dark ? '#0b1220' : '#ffffff',
    inputText:    dark ? '#e5e7eb' : '#0f172a',
    inputBorder:  dark ? '#334155' : '#cbd5e1',
    // accent / semantic
    accent:       '#0ea5e9',
    accentSoft:   dark ? 'rgba(14,165,233,0.15)' : '#e0f2fe',
    danger:       dark ? '#f87171' : '#b91c1c',
    ok:           '#22c55e',
    // pill helpers
    pillOnBg:     dark ? 'rgba(34,197,94,0.20)' : '#dcfce7',
    pillOnFg:     dark ? '#bbf7d0' : '#166534',
    pillOffBg:    dark ? 'rgba(148,163,184,0.18)' : '#f1f5f9',
    pillOffFg:    dark ? '#cbd5e1' : '#475569',
  };
}

// Reusable toggle button. Pass in [theme, setTheme] from useTheme().
export function ThemeToggleButton({ theme, setTheme, style }) {
  const t = tokens(theme);
  return (
    <button
      type="button"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      style={{
        padding: '6px 12px',
        borderRadius: 10,
        border: `1px solid ${t.borderStrong}`,
        background: t.cardAlt,
        color: t.text,
        cursor: 'pointer',
        fontSize: 14,
        ...style,
      }}
      aria-label="Toggle theme"
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
    </button>
  );
}
